import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw, Trash2, Search, Ticket, Users as UsersIcon,
  ArrowLeft, ShieldCheck, Mail, Activity, Watch, ImageIcon, Clock, Palette,
  Star, Check, X as XIcon, BarChart3, Copy, ChevronDown, ChevronRight,
  PartyPopper, Percent, Euro, Hash, Sparkles, CalendarDays, HardDrive,
  Package, Truck, PackageCheck, CircleDot, Send, ArrowRight, Download, Loader2,
} from 'lucide-react';
import AdminCachePanel from './AdminCachePanel';
import { Card, CardHeader, CardTitle, CardContent } from "./card";
import { Input } from "./input";
import { Button } from "./button";
import { Alert, AlertDescription } from "./alert";
import { Checkbox } from "./checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { AdminImageSlideshow } from './imageslideshow';
import { PRICES } from '../config/prices';
import { Stars } from './ReviewsList';
import { formatSyncDate } from '../utils/dateFormat';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Compact relative time used in user cards. Falls back to a date string for
// anything older than ~30 days because "73d ago" is harder to scan than the
// actual date.
const relativeTime = (iso) => {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Never';
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return 'just now';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day <= 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
};

const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${className}`}>
    {children}
  </span>
);

// Format a voucher's discount as a single human string. The DB stores
// percentages as fractions (0.20) and amounts as integer cents.
const formatVoucherDiscount = (voucher) => {
  if (!voucher) return '';
  if (voucher.discountType === 'amount') {
    const cents = voucher.discountAmountCents || 0;
    return `€${(cents / 100).toFixed(2)} off`;
  }
  const pct = (voucher.discountPercent || 0) * 100;
  return `${pct.toFixed(0)}% off`;
};

// Split vouchers into the three buckets shown in the admin list. Order of
// checks matters: we treat fully-redeemed as "used" before "expired" so a
// successfully-spent voucher doesn't get filed under expired even if its
// expiry date has also passed (the success outcome is what matters).
const groupVouchers = (vouchers) => {
  // Bought (logged-in user) + bought public (anonymous) + remainder all stay
  // grouped by source regardless of redemption state. Admin-generated codes
  // (no source) split further into active / used / expired.
  const now = Date.now();
  const boughtUser = [];
  const boughtPublic = [];
  const boughtPublicUsed = [];
  const remainder = [];
  const adminActive = [];
  const adminUsed = [];
  const adminExpired = [];
  for (const v of vouchers) {
    const max = typeof v.maxUses === 'number' ? v.maxUses : Infinity;
    const isUsedUp = (v.currentUses || 0) >= max;
    if (v.source === 'purchase') {
      if (v.purchasedBy) boughtUser.push(v);
      else if (isUsedUp) boughtPublicUsed.push(v);
      else boughtPublic.push(v);
      continue;
    }
    if (v.source === 'remainder') { remainder.push(v); continue; }
    if (isUsedUp) adminUsed.push(v);
    else if (v.expiryDate && new Date(v.expiryDate).getTime() < now) adminExpired.push(v);
    else adminActive.push(v);
  }
  return { boughtUser, boughtPublic, boughtPublicUsed, remainder, adminActive, adminUsed, adminExpired };
};

const VoucherCard = ({ voucher, expandedVoucherId, setExpandedVoucherId }) => {
  const id = voucher._id || voucher.id;
  const isExpanded = expandedVoucherId === id;
  const usedBy = Array.isArray(voucher.usedBy) ? voucher.usedBy : [];
  const max = typeof voucher.maxUses === 'number' ? voucher.maxUses : Infinity;
  const isExpired = voucher.expiryDate && new Date(voucher.expiryDate).getTime() < Date.now();
  const isUsedUp = (voucher.currentUses || 0) >= max;
  const accent = isUsedUp
    ? 'border-blue-200 bg-blue-50/40'
    : isExpired
      ? 'border-gray-200 bg-gray-50'
      : voucher.active
        ? 'border-green-200 bg-white'
        : 'border-red-200 bg-red-50/40';

  return (
    <div className={`rounded-xl border ${accent} p-3 space-y-2`}>
      <div className="flex items-start justify-between gap-2">
        <code className="font-mono text-sm font-semibold break-all">{voucher.code}</code>
        <Badge
          className={
            isUsedUp
              ? 'bg-blue-100 text-blue-700'
              : isExpired
                ? 'bg-gray-200 text-gray-700'
                : voucher.active
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
          }
        >
          {isUsedUp ? 'Used up' : isExpired ? 'Expired' : voucher.active ? 'Active' : 'Inactive'}
        </Badge>
      </div>
      <div className="text-xs text-gray-700 space-y-0.5">
        <p>{formatVoucherDiscount(voucher)}</p>
        <p>
          Uses: <span className="font-medium">{voucher.currentUses || 0}</span> / {voucher.maxUses}
        </p>
        <p>
          {voucher.expiryDate
            ? `Valid until ${new Date(voucher.expiryDate).toLocaleDateString()}`
            : 'Never expires'}
        </p>
        {voucher.source === 'purchase' && (
          <p className="text-gray-500">
            {voucher.purchasedBy ? (
              <>Bought by {voucher.purchasedByEmail || voucher.purchasedBy}</>
            ) : (
              <>Bought publicly by {voucher.buyerEmail || 'unknown'}</>
            )}
            {voucher.createdAt && ` on ${new Date(voucher.createdAt).toLocaleDateString()}`}
          </p>
        )}
        {voucher.source === 'remainder' && (
          <p className="text-gray-500">
            Leftover from <code className="font-mono">{voucher.originVoucherCode || '—'}</code>
            {voucher.purchasedByEmail && <> · for {voucher.purchasedByEmail}</>}
          </p>
        )}
      </div>
      {(voucher.currentUses || 0) > 0 && (
        <div className="pt-1 border-t border-gray-200/70">
          <button
            type="button"
            onClick={() => setExpandedVoucherId(isExpanded ? null : id)}
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
          >
            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Used by {usedBy.length || voucher.currentUses}
          </button>
          {isExpanded && (
            <div className="mt-1 max-h-40 overflow-y-auto space-y-1">
              {usedBy.length === 0 ? (
                <p className="text-[11px] text-gray-500 italic">Usage details unavailable.</p>
              ) : (
                usedBy.map((u, i) => (
                  <div
                    key={`${u.userId || 'anon'}-${i}`}
                    className="flex items-center justify-between gap-2 text-[11px] bg-white border border-gray-200 rounded px-2 py-1"
                  >
                    <span className="truncate font-mono">{u.email || u.userId || 'unknown'}</span>
                    {u.usedAt && (
                      <span className="text-gray-500 shrink-0">
                        {new Date(u.usedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const VoucherSection = ({ title, color, vouchers, expandedVoucherId, setExpandedVoucherId, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center w-full text-left gap-2 mb-2"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className={`text-xs px-2 py-0.5 rounded-full ${color}`}>{vouchers.length}</span>
      </button>
      {open && (
        vouchers.length === 0 ? (
          <p className="text-xs text-gray-500 italic pl-6 pb-2">None.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {vouchers.map((v) => (
              <VoucherCard
                key={v._id || v.id}
                voucher={v}
                expandedVoucherId={expandedVoucherId}
                setExpandedVoucherId={setExpandedVoucherId}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

const VoucherList = ({ vouchers, loading, expandedVoucherId, setExpandedVoucherId }) => {
  const groups = useMemo(() => groupVouchers(vouchers), [vouchers]);
  return (
    <div className="border rounded-lg bg-background p-4 max-h-[60vh] overflow-y-auto space-y-4">
      <div className="flex flex-wrap gap-2 text-xs pb-2 border-b border-gray-200">
        <Badge className="bg-purple-100 text-purple-700">{groups.boughtUser.length} bought (user)</Badge>
        <Badge className="bg-pink-100 text-pink-700">{groups.boughtPublic.length} bought (public)</Badge>
        <Badge className="bg-rose-100 text-rose-700">{groups.boughtPublicUsed.length} bought (public, used)</Badge>
        <Badge className="bg-amber-100 text-amber-700">{groups.remainder.length} remainder</Badge>
        <Badge className="bg-green-100 text-green-700">{groups.adminActive.length} active</Badge>
        <Badge className="bg-blue-100 text-blue-700">{groups.adminUsed.length} used up</Badge>
        <Badge className="bg-gray-200 text-gray-700">{groups.adminExpired.length} expired</Badge>
      </div>
      <VoucherSection
        title="Bought by users"
        color="bg-purple-100 text-purple-700"
        vouchers={groups.boughtUser}
        expandedVoucherId={expandedVoucherId}
        setExpandedVoucherId={setExpandedVoucherId}
      />
      <VoucherSection
        title="Bought publicly (no login)"
        color="bg-pink-100 text-pink-700"
        vouchers={groups.boughtPublic}
        expandedVoucherId={expandedVoucherId}
        setExpandedVoucherId={setExpandedVoucherId}
      />
      <VoucherSection
        title="Bought publicly · used"
        color="bg-rose-100 text-rose-700"
        vouchers={groups.boughtPublicUsed}
        expandedVoucherId={expandedVoucherId}
        setExpandedVoucherId={setExpandedVoucherId}
        defaultOpen={false}
      />
      <VoucherSection
        title="Remainder vouchers"
        color="bg-amber-100 text-amber-700"
        vouchers={groups.remainder}
        expandedVoucherId={expandedVoucherId}
        setExpandedVoucherId={setExpandedVoucherId}
      />
      <VoucherSection
        title="Admin-generated · active"
        color="bg-green-100 text-green-700"
        vouchers={groups.adminActive}
        expandedVoucherId={expandedVoucherId}
        setExpandedVoucherId={setExpandedVoucherId}
      />
      <VoucherSection
        title="Admin-generated · used up"
        color="bg-blue-100 text-blue-700"
        vouchers={groups.adminUsed}
        expandedVoucherId={expandedVoucherId}
        setExpandedVoucherId={setExpandedVoucherId}
        defaultOpen={false}
      />
      <VoucherSection
        title="Admin-generated · expired"
        color="bg-gray-200 text-gray-700"
        vouchers={groups.adminExpired}
        expandedVoucherId={expandedVoucherId}
        setExpandedVoucherId={setExpandedVoucherId}
        defaultOpen={false}
      />
      {loading && <div className="text-center py-2 text-gray-500">Loading…</div>}
      {!loading && vouchers.length === 0 && (
        <div className="text-center py-6 text-gray-500">No vouchers found.</div>
      )}
    </div>
  );
};

// Small thumbnail strip rendered below a user card when expanded. Pulls
// /admin/users/<email>/recent_maps once on mount; the response inlines the
// watermark as base64 so no per-thumbnail fetch is needed.
const RecentMapsStrip = ({ userEmail }) => {
  const [maps, setMaps] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/recent_maps`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
        );
        if (!r.ok) throw new Error('Failed to load recent maps');
        const data = await r.json();
        if (!cancelled) setMaps(data.maps || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [userEmail]);

  if (error) {
    return <p className="text-sm text-red-500">Failed to load recent maps: {error}</p>;
  }
  if (maps === null) {
    return <p className="text-sm text-gray-500">Loading recent maps…</p>;
  }
  if (maps.length === 0) {
    return <p className="text-sm text-gray-500">No recent maps.</p>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
      {maps.map((m) => (
        <div key={m.id} className="rounded-xl overflow-hidden border border-gray-200 bg-white">
          <img
            src={m.imageUrl}
            alt={m.place_name || 'recent map'}
            className="w-full aspect-square object-cover"
          />
          <div className="p-2 text-[11px] text-gray-600 space-y-0.5">
            <div className="font-medium text-gray-800 truncate">{m.place_name || '—'}</div>
            <div>{m.km2 ? `${m.km2} km²` : '—'}{m.generated_size ? ` · ${m.generated_size}` : ''}</div>
            <div className="text-gray-400">{relativeTime(m.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const UserCard = ({ user, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md bg-white transition-colors p-4 flex items-start gap-3"
    >
      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center font-semibold flex-shrink-0">
        {user.email?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 truncate">{user.email}</span>
          {user.is_admin && <Badge className="bg-amber-100 text-amber-700"><ShieldCheck className="h-3 w-3" />admin</Badge>}
          {user.is_verified
            ? <Badge className="bg-green-100 text-green-700"><Mail className="h-3 w-3" />verified</Badge>
            : <Badge className="bg-gray-100 text-gray-500"><Mail className="h-3 w-3" />unverified</Badge>
          }
          {user.strava_connected && <Badge className="bg-orange-100 text-orange-700"><Activity className="h-3 w-3" />strava</Badge>}
          {user.garmin_connected && <Badge className="bg-sky-100 text-sky-700"><Watch className="h-3 w-3" />garmin</Badge>}
        </div>
        <div className="mt-1.5 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            last login {relativeTime(user.last_login)}
          </span>
          <span>{user.num_logins ?? 0} logins</span>
          <span className="inline-flex items-center gap-1">
            <ImageIcon className="h-3 w-3" />
            {user.num_images ?? 0} paid · {user.num_recent_images ?? 0} recent
          </span>
          <span className="text-gray-400">joined {relativeTime(user.created_at)}</span>
        </div>
      </div>
    </button>
  );
};

const ORDER_STATE_META_ADMIN = {
  received:   { label: 'Received',   color: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CircleDot },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700 border-blue-200',           Icon: Package },
  shipping:   { label: 'Shipping',   color: 'bg-amber-100 text-amber-700 border-amber-200',        Icon: Truck },
  completed:  { label: 'Completed',  color: 'bg-gray-100 text-gray-700 border-gray-200',           Icon: PackageCheck },
};

const ORDER_STATE_ORDER = ['received', 'processing', 'shipping', 'completed'];

// Lazy-loaded order preview thumb. The endpoint streams the recent_images
// watermark; we pull it as a blob so the admin token (in localStorage)
// can be sent in the Authorization header (img.src can't carry headers).
const OrderImagePreview = ({ orderId }) => {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/admin/print-orders/${orderId}/image`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) throw new Error('no image');
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (_e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [orderId]);

  if (error) {
    return (
      <div className="w-32 h-24 rounded-md bg-gray-100 flex items-center justify-center text-gray-400 text-[10px]">
        no preview
      </div>
    );
  }
  if (!src) {
    return <div className="w-32 h-24 rounded-md bg-gray-100 animate-pulse" />;
  }
  return (
    <img
      src={src}
      alt="Order preview"
      className="w-32 h-24 object-cover rounded-md border border-gray-200"
    />
  );
};

const PrintOrderRow = ({ order, busy, onAdvance, onEmail }) => {
  // Local input state so the admin can type without triggering refetches.
  // Initialised from the order, but kept in local state until they save.
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '');
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || '');
  const [copied, setCopied] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Download the full PNG via an auth'd fetch (img src can't carry the
  // bearer token, and using a query-param token would log it). Built as
  // a blob → object URL → synthetic <a download> click.
  const downloadFullImage = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${API_URL}/admin/print-orders/${order.id}/full`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error('Failed to download');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Server's Content-Disposition supplies the filename; <a download>
      // attr is the fallback if the header gets stripped.
      a.download = `mapprints_${order.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('Order download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  // Re-sync when the parent reloads with a fresh order doc.
  useEffect(() => { setTrackingNumber(order.tracking_number || ''); }, [order.tracking_number]);
  useEffect(() => { setTrackingUrl(order.tracking_url || ''); }, [order.tracking_url]);

  const meta = ORDER_STATE_META_ADMIN[order.state] || ORDER_STATE_META_ADMIN.received;
  const Icon = meta.Icon;
  const addr = order.shipping_address || {};
  const addrLines = [
    order.shipping_name,
    addr.line1,
    addr.line2,
    [addr.postal_code, addr.city].filter(Boolean).join(' '),
    addr.country,
  ].filter(Boolean);

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(c => (c === key ? null : c)), 1200);
    } catch (_e) { /* no-op */ }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={meta.color}><Icon className="h-3 w-3" />{meta.label}</Badge>
          <span className="font-medium text-sm">Size {order.size}</span>
          {order.frame && order.frame !== 'none' && (
            <span className="text-xs text-gray-600">· {order.frame.replace('_', ' ')}{order.frame_color ? ` (${order.frame_color})` : ''}</span>
          )}
          {order.passepartout_cm > 0 && (
            <span className="text-xs text-gray-600">· passepartout {order.passepartout_cm}cm</span>
          )}
          {order.voucher_code && (
            <Badge className="bg-purple-100 text-purple-700"><Ticket className="h-3 w-3" />{order.voucher_code}</Badge>
          )}
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>€{((order.amount_cents || 0) / 100).toFixed(2)}</div>
          <div>{order.created_at ? new Date(order.created_at).toLocaleString() : '—'}</div>
        </div>
      </div>

      {/* Image preview + reference IDs — what the admin needs to actually
          place the order with the print shop. */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="flex flex-col gap-1.5">
          <OrderImagePreview orderId={order.id} />
          <Button
            variant="outline"
            size="sm"
            disabled={downloading}
            onClick={downloadFullImage}
            className="text-xs h-7"
          >
            {downloading ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Download className="h-3 w-3 mr-1" />
            )}
            Download PNG
          </Button>
        </div>
        <div className="flex-1 min-w-[12rem] text-xs space-y-1">
          {order.place_name && (
            <div className="text-sm font-medium text-gray-900">{order.place_name}</div>
          )}
          {order.heigth && order.width && (
            <div className="text-gray-600">{order.heigth}×{order.width} cm</div>
          )}
          <div className="flex items-center gap-1 text-gray-500">
            <span className="uppercase tracking-wide">User</span>
            <code className="font-mono text-gray-800 break-all">{order.user_id || '—'}</code>
            {order.user_id && (
              <button
                type="button"
                onClick={() => copyToClipboard(order.user_id, 'uid')}
                className="text-gray-400 hover:text-gray-700"
                title="Copy user id"
              >
                {copied === 'uid' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 text-gray-500">
            <span className="uppercase tracking-wide">Image</span>
            <code className="font-mono text-gray-800 break-all">{order.image_id || '—'}</code>
            {order.image_id && (
              <button
                type="button"
                onClick={() => copyToClipboard(order.image_id, 'iid')}
                className="text-gray-400 hover:text-gray-700"
                title="Copy image id"
              >
                {copied === 'iid' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-xs">
        <div>
          <div className="text-gray-500 uppercase tracking-wide mb-1">Customer</div>
          <div className="text-gray-900 break-words">
            {order.user_email || '—'}<br />
            {order.customer_email && order.customer_email !== order.user_email && (
              <span className="text-gray-600">{order.customer_email}<br /></span>
            )}
            {order.customer_phone || ''}
          </div>
        </div>
        <div>
          <div className="text-gray-500 uppercase tracking-wide mb-1">Shipping</div>
          <div className="text-gray-900 break-words">
            {addrLines.length > 0 ? addrLines.map((l, i) => <div key={i}>{l}</div>) : '—'}
            {order.shipping_rate_label && (
              <div className="text-gray-500 mt-1">{order.shipping_rate_label}</div>
            )}
          </div>
        </div>
      </div>

      {/* State-specific action area. The states are sequential, so each
          state shows only the next action the admin needs. */}
      {order.state === 'received' && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
          <Button
            variant="black"
            size="sm"
            disabled={busy}
            onClick={() => onAdvance('processing', null, null)}
            className="text-xs"
          >
            <ArrowRight className="h-3 w-3 mr-1" />
            Move to Processing
          </Button>
          <span className="text-[11px] text-gray-500">
            Click after you've placed the order with the print shop.
          </span>
        </div>
      )}

      {order.state === 'processing' && (
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Tracking number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md"
            />
            <input
              type="url"
              placeholder="Tracking URL (https://…)"
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="black"
              size="sm"
              disabled={busy || !trackingNumber.trim()}
              onClick={() => onAdvance('shipping', trackingNumber.trim(), trackingUrl.trim() || null)}
              className="text-xs"
            >
              <Truck className="h-3 w-3 mr-1" />
              Move to Shipping
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onEmail('processing')}
              className="text-xs"
            >
              <Send className="h-3 w-3 mr-1" />
              Email "processing"
            </Button>
          </div>
        </div>
      )}

      {order.state === 'shipping' && (
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <div className="text-xs">
            <div className="text-gray-500 uppercase tracking-wide mb-1">Tracking</div>
            <div className="font-mono break-all">{order.tracking_number || '—'}</div>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {order.tracking_url}
              </a>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Tracking number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md"
            />
            <input
              type="url"
              placeholder="Tracking URL"
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(trackingNumber !== (order.tracking_number || '') || trackingUrl !== (order.tracking_url || '')) && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => onAdvance('shipping', trackingNumber.trim(), trackingUrl.trim() || null)}
                className="text-xs"
              >
                Save tracking
              </Button>
            )}
            <Button
              variant="black"
              size="sm"
              disabled={busy}
              onClick={() => onAdvance('completed', null, null)}
              className="text-xs"
            >
              <PackageCheck className="h-3 w-3 mr-1" />
              Mark as Completed
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onEmail('shipping')}
              className="text-xs"
            >
              <Send className="h-3 w-3 mr-1" />
              Email "shipped"
            </Button>
          </div>
        </div>
      )}

      {order.state === 'completed' && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
          {order.tracking_number && (
            <span className="text-xs text-gray-600">
              Tracking: <code className="font-mono">{order.tracking_number}</code>
              {order.tracking_url && (
                <> · <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">link</a></>
              )}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => onEmail('completed')}
            className="text-xs ml-auto"
          >
            <Send className="h-3 w-3 mr-1" />
            Email "delivered"
          </Button>
        </div>
      )}
    </div>
  );
};

const UserDetailView = ({ user, onBack }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3">
      <Button
        variant="outline"
        onClick={onBack}
        className="flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to users
      </Button>
    </div>

    <div className="rounded-xl border border-gray-200 bg-white p-4 flex items-start gap-3">
      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center font-semibold flex-shrink-0 text-lg">
        {user.email?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900 truncate">{user.email}</span>
          {user.is_admin && <Badge className="bg-amber-100 text-amber-700"><ShieldCheck className="h-3 w-3" />admin</Badge>}
          {user.is_verified
            ? <Badge className="bg-green-100 text-green-700"><Mail className="h-3 w-3" />verified</Badge>
            : <Badge className="bg-gray-100 text-gray-500"><Mail className="h-3 w-3" />unverified</Badge>
          }
          {user.strava_connected && <Badge className="bg-orange-100 text-orange-700"><Activity className="h-3 w-3" />strava</Badge>}
          {user.garmin_connected && <Badge className="bg-sky-100 text-sky-700"><Watch className="h-3 w-3" />garmin</Badge>}
        </div>
        <div className="mt-1.5 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            last login {relativeTime(user.last_login)}
          </span>
          <span>{user.num_logins ?? 0} logins</span>
          <span className="inline-flex items-center gap-1">
            <ImageIcon className="h-3 w-3" />
            {user.num_images ?? 0} paid · {user.num_recent_images ?? 0} recent
          </span>
          <span className="text-gray-400">joined {relativeTime(user.created_at)}</span>
        </div>
      </div>
    </div>

    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent (last 5 generated)</h4>
      <RecentMapsStrip userEmail={user.email} />
    </div>
    <UserPrintOrdersStrip userEmail={user.email} />
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Unlocked maps</h4>
      <AdminImageSlideshow userEmail={user.email} />
    </div>
  </div>
);

// Per-user print orders. Mirrors RecentMapsStrip's standalone-fetch
// pattern so it doesn't bloat UserDetailView's state. Renders compact
// rows: state pill + size + frame + tracking + created date.
const UserPrintOrdersStrip = ({ userEmail }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/admin/users/${encodeURIComponent(userEmail)}/print-orders`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
        );
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) setOrders(data.orders || []);
      } catch (_e) { /* render empty state */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [userEmail]);

  if (loading) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Print orders</h4>
        <div className="text-xs text-gray-500">Loading…</div>
      </div>
    );
  }
  if (orders.length === 0) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Print orders</h4>
        <div className="text-xs text-gray-500">No print orders.</div>
      </div>
    );
  }
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Print orders ({orders.length})</h4>
      <div className="space-y-1.5">
        {orders.map(o => {
          const meta = ORDER_STATE_META_ADMIN[o.state] || ORDER_STATE_META_ADMIN.received;
          const Icon = meta.Icon;
          return (
            <div key={o.id} className="flex items-center gap-2 text-xs p-2 rounded-lg border border-gray-200 bg-white flex-wrap">
              <Badge className={meta.color}><Icon className="h-3 w-3" />{meta.label}</Badge>
              <span className="font-medium">Size {o.size}</span>
              {o.frame && o.frame !== 'none' && (
                <span className="text-gray-600">{o.frame.replace('_', ' ')}{o.frame_color ? ` (${o.frame_color})` : ''}</span>
              )}
              {o.tracking_number && (
                <span className="font-mono text-gray-700">#{o.tracking_number}</span>
              )}
              <span className="text-gray-400 ml-auto">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Small reusable bits for the Stats tab. Kept inline because they're trivial
// and only used here — extracting would be premature.
const StatCard = ({ label, value, hint }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col gap-1">
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-semibold text-gray-900">{value ?? '—'}</div>
    {hint && <div className="text-xs text-gray-400">{hint}</div>}
  </div>
);

const Histogram = ({ title, buckets, unit, formatBucket }) => {
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0) || 1;
  // Trim trailing zero buckets so the last visible bar is meaningful.
  let lastNonZero = -1;
  for (let i = 0; i < buckets.length; i++) if (buckets[i].count > 0) lastNonZero = i;
  const visible = lastNonZero >= 0 ? buckets.slice(0, lastNonZero + 1) : buckets;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="text-sm font-semibold mb-3">{title}</div>
      {visible.every((b) => b.count === 0) ? (
        <p className="text-xs text-gray-400">No data yet.</p>
      ) : (
        <div className="space-y-1">
          {visible.map((b, i) => {
            const isLast = i === visible.length - 1 && i === buckets.length - 1;
            const pct = (b.count / max) * 100;
            return (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className="w-20 text-right text-gray-600 font-mono">
                  {formatBucket(b.min, isLast)}
                </div>
                <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                  <div
                    className="h-full bg-gray-800"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="w-10 text-right text-gray-700 tabular-nums">{b.count}</div>
              </div>
            );
          })}
        </div>
      )}
      {unit && <div className="text-[10px] text-gray-400 mt-2">{unit}</div>}
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
      active
        ? 'bg-gray-900 text-white shadow-sm'
        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
    {count != null && (
      <span className={`text-xs ${active ? 'text-gray-300' : 'text-gray-400'}`}>({count})</span>
    )}
  </button>
);

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('users');

  // Users tab state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);

  // Shared-styles tab state
  const [sharedStyles, setSharedStyles] = useState([]);
  const [loadingSharedStyles, setLoadingSharedStyles] = useState(false);
  const [deletingStyleId, setDeletingStyleId] = useState(null);

  // Reviews tab state
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('pending');
  const [reviewBusyId, setReviewBusyId] = useState(null);

  // Stats tab state
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Print orders tab state
  const [printOrders, setPrintOrders] = useState([]);
  const [loadingPrintOrders, setLoadingPrintOrders] = useState(false);
  const [orderStateFilter, setOrderStateFilter] = useState('open'); // open|all|received|processing|shipping|completed
  const [orderActionBusy, setOrderActionBusy] = useState(null);

  const fetchPrintOrders = async () => {
    setLoadingPrintOrders(true);
    try {
      let url = `${API_URL}/admin/print-orders`;
      if (orderStateFilter && orderStateFilter !== 'open' && orderStateFilter !== 'all') {
        url += `?state=${orderStateFilter}`;
      }
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error('Failed to load orders');
      const data = await r.json();
      let orders = data.orders || [];
      if (orderStateFilter === 'open') {
        orders = orders.filter(o => o.state !== 'completed');
      }
      setPrintOrders(orders);
    } catch (e) {
      console.error(e);
      setPrintOrders([]);
    } finally {
      setLoadingPrintOrders(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'orders') fetchPrintOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, orderStateFilter]);

  // Advance to a specific state. tracking_number / tracking_url are
  // optional and only meaningful for `shipping`, but the backend accepts
  // them on any state set (useful for fixing typos after the fact).
  const advanceOrder = async (orderId, nextState, trackingNumber, trackingUrl) => {
    setOrderActionBusy(orderId);
    try {
      const r = await fetch(`${API_URL}/admin/print-orders/${orderId}/state`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state: nextState,
          tracking_number: trackingNumber || null,
          tracking_url: trackingUrl || null,
        }),
      });
      if (!r.ok) throw new Error('Failed to advance order');
      await fetchPrintOrders();
    } catch (e) {
      console.error(e);
    } finally {
      setOrderActionBusy(null);
    }
  };

  const emailOrder = async (orderId, template) => {
    setOrderActionBusy(orderId);
    try {
      const r = await fetch(`${API_URL}/admin/print-orders/${orderId}/email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ template }),
      });
      if (!r.ok) throw new Error('Failed to send email');
      await fetchPrintOrders();
    } catch (e) {
      console.error(e);
    } finally {
      setOrderActionBusy(null);
    }
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const r = await fetch(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error('Failed to load stats');
      const data = await r.json();
      setStats(data);
    } catch (e) {
      console.error(e);
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'stats') fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchReviews = async (filter = reviewFilter) => {
    setLoadingReviews(true);
    try {
      const r = await fetch(`${API_URL}/admin/reviews?status=${filter}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error('Failed to load reviews');
      const data = await r.json();
      setReviews(data.reviews || []);
    } catch (e) {
      console.error(e);
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const setReviewApproval = async (id, approved) => {
    setReviewBusyId(id);
    try {
      const path = approved ? 'approve' : 'unapprove';
      const r = await fetch(`${API_URL}/admin/reviews/${id}/${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error('Failed to update review');
      await fetchReviews();
    } catch (e) {
      console.error(e);
    } finally {
      setReviewBusyId(null);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Permanently delete this review?')) return;
    setReviewBusyId(id);
    try {
      const r = await fetch(`${API_URL}/admin/reviews/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error('Failed to delete review');
      await fetchReviews();
    } catch (e) {
      console.error(e);
    } finally {
      setReviewBusyId(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'reviews') fetchReviews(reviewFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reviewFilter]);

  // Vouchers tab state (preserved from the previous panel)
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [voucherToDelete, setVoucherToDelete] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState(null);
  const [generatedSummary, setGeneratedSummary] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedVoucherId, setExpandedVoucherId] = useState(null);
  const [newVoucher, setNewVoucher] = useState({
    discountType: 'percent',
    discount_percent: 20,
    discount_amount_cents: 99,
    max_uses: 1,
    valid_days: 30,
    prefix: '',
    random: true,
  });

  const fetchVouchers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/admin/vouchers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      if (response.ok) setVouchers(data.vouchers || []);
      else setError(data.error || 'Failed to fetch vouchers');
    } catch (_err) {
      setError('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      if (response.ok) setUsers(data.users || []);
      else setError(data.error || 'Failed to fetch users');
    } catch (_err) {
      setError('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const deleteVoucher = async () => {
    if (!voucherToDelete.trim()) return;
    try {
      const response = await fetch(`${API_URL}/admin/vouchers/${voucherToDelete}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        fetchVouchers();
        setVoucherToDelete('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete voucher');
      }
    } catch (_err) {
      setError('Failed to delete voucher');
    }
  };

  const generateVouchers = async () => {
    try {
      const voucherData = {
        discountType: newVoucher.discountType,
        max_uses: newVoucher.max_uses,
        valid_days: isUnlimited ? null : newVoucher.valid_days,
        prefix: newVoucher.prefix,
        random: newVoucher.random,
      };
      if (newVoucher.discountType === 'percent') {
        voucherData.discount_percent = newVoucher.discount_percent;
      } else {
        voucherData.discount_amount_cents = newVoucher.discount_amount_cents;
      }

      const response = await fetch(`${API_URL}/admin/generate-vouchers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(voucherData),
      });
      const data = await response.json();
      if (response.ok) {
        fetchVouchers();
        setError(null);
        setGeneratedCodes(data.codes || []);
        // Snapshot the form state at generation time so the popup can show
        // "20% · 1 use · 30 days" without having to re-derive it later (the
        // form may already have been edited by the time the user reads it).
        setGeneratedSummary({
          discountType: newVoucher.discountType,
          discountPercent: newVoucher.discount_percent,
          discountAmountCents: newVoucher.discount_amount_cents,
          maxUses: newVoucher.max_uses,
          validDays: isUnlimited ? null : newVoucher.valid_days,
        });
      } else {
        setError(data.error || 'Failed to generate vouchers');
      }
    } catch (_err) {
      setError('Failed to generate vouchers');
    }
  };

  const copyCodeToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch (_e) {
      // Clipboard API can be blocked in non-secure contexts; surface nothing
      // — the code is visible and selectable in the dialog.
    }
  };

  const fetchSharedStyles = async () => {
    setLoadingSharedStyles(true);
    try {
      const r = await fetch(`${API_URL}/admin/shared_styles?page=0&limit=100`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await r.json();
      if (r.ok) setSharedStyles(data.styles || []);
      else setError(data.error || 'Failed to fetch shared styles');
    } catch (_e) {
      setError('Failed to fetch shared styles');
    } finally {
      setLoadingSharedStyles(false);
    }
  };

  const deleteSharedStyle = async (styleId) => {
    if (deletingStyleId) return;
    setDeletingStyleId(styleId);
    try {
      const r = await fetch(`${API_URL}/admin/shared_styles/${styleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.error || 'Failed to delete style');
        return;
      }
      setSharedStyles((prev) => prev.filter((s) => s.id !== styleId));
    } catch (_e) {
      setError('Failed to delete style');
    } finally {
      setDeletingStyleId(null);
    }
  };

  useEffect(() => {
    fetchVouchers();
    fetchUsers();
    fetchSharedStyles();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.email || '').toLowerCase().includes(q));
  }, [users, userSearch]);

  const selectedUser = useMemo(
    () => (expandedUserId ? users.find((u) => u.id === expandedUserId) || null : null),
    [users, expandedUserId],
  );

  return (
    <div className="p-2 lg:p-4 space-y-4 h-full flex flex-col min-h-0">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2 flex-wrap">
        <TabButton
          active={activeTab === 'users'}
          onClick={() => setActiveTab('users')}
          icon={UsersIcon}
          label="Users"
          count={users.length}
        />
        <TabButton
          active={activeTab === 'orders'}
          onClick={() => setActiveTab('orders')}
          icon={Package}
          label="Orders"
          count={printOrders.length}
        />
        <TabButton
          active={activeTab === 'vouchers'}
          onClick={() => setActiveTab('vouchers')}
          icon={Ticket}
          label="Vouchers"
          count={vouchers.length}
        />
        <TabButton
          active={activeTab === 'shared_styles'}
          onClick={() => setActiveTab('shared_styles')}
          icon={Palette}
          label="Shared styles"
          count={sharedStyles.length}
        />
        <TabButton
          active={activeTab === 'reviews'}
          onClick={() => setActiveTab('reviews')}
          icon={Star}
          label="Reviews"
          count={reviews.length}
        />
        <TabButton
          active={activeTab === 'stats'}
          onClick={() => setActiveTab('stats')}
          icon={BarChart3}
          label="Stats"
        />
        <TabButton
          active={activeTab === 'cache'}
          onClick={() => setActiveTab('cache')}
          icon={HardDrive}
          label="Cache"
        />
      </div>

      {activeTab === 'users' && (
        selectedUser ? (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
            <UserDetailView
              user={selectedUser}
              onBack={() => setExpandedUserId(null)}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col space-y-3">
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search users by email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 w-full"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={fetchUsers}
                disabled={loadingUsers}
                title="Refresh users"
                className="flex-shrink-0"
              >
                <RefreshCw className={`h-4 w-4 ${loadingUsers ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {loadingUsers && users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading users…</div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {userSearch ? `No users match "${userSearch}"` : 'No users found.'}
              </div>
            ) : (
              // Each row is ~5rem tall (card padding + avatar + 2 lines of meta);
              // 5 rows + gaps ≈ 28rem. Hard cap so the grid scrolls regardless
              // of what the surrounding layout chain does.
              <div className="overflow-y-auto pr-1 -mr-1 max-h-[28rem]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {filteredUsers.map((user) => (
                    <UserCard
                      key={user.id}
                      user={user}
                      onClick={() => setExpandedUserId(user.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}

      {activeTab === 'orders' && (
        <div className="space-y-3 flex-1 min-h-0 flex flex-col">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="flex gap-1.5 flex-wrap">
              {[
                { id: 'open',       label: 'Open' },
                { id: 'received',   label: 'Received' },
                { id: 'processing', label: 'Processing' },
                { id: 'shipping',   label: 'Shipping' },
                { id: 'completed',  label: 'Completed' },
                { id: 'all',        label: 'All' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setOrderStateFilter(f.id)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    orderStateFilter === f.id
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchPrintOrders}
              disabled={loadingPrintOrders}
              title="Refresh orders"
            >
              <RefreshCw className={`h-4 w-4 ${loadingPrintOrders ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
            {loadingPrintOrders && printOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading orders…</div>
            ) : printOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No orders match this filter.</div>
            ) : (
              <div className="space-y-2">
                {printOrders.map(order => (
                  <PrintOrderRow
                    key={order.id}
                    order={order}
                    busy={orderActionBusy === order.id}
                    onAdvance={(next, tn, tu) => advanceOrder(order.id, next, tn, tu)}
                    onEmail={(template) => emailOrder(order.id, template)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'shared_styles' && (
        <div className="space-y-3 flex-1 min-h-0 flex flex-col">
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="icon"
              onClick={fetchSharedStyles}
              disabled={loadingSharedStyles}
              title="Refresh shared styles"
            >
              <RefreshCw className={`h-4 w-4 ${loadingSharedStyles ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {loadingSharedStyles && sharedStyles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Loading shared styles…</div>
          ) : sharedStyles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No shared styles.</div>
          ) : (
            <div className="overflow-y-auto pr-1 -mr-1 max-h-[28rem]">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {sharedStyles.map((s) => (
                  <div key={s.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col">
                    <div className="aspect-[4/3] bg-gray-100">
                      <img
                        src={`${API_URL}/shared_styles/${s.id}/preview`}
                        alt={s.style_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3 flex-1 flex flex-col gap-1">
                      <div className="font-medium text-sm text-gray-900 truncate">{s.style_name}</div>
                      <div className="text-xs text-gray-600 truncate">by {s.display_name}</div>
                      {s.owner_email && (
                        <div className="text-[11px] text-gray-400 truncate" title={s.owner_email}>
                          {s.owner_email}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-400">
                        {relativeTime(s.created_at)}
                      </div>
                      <div className="mt-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteSharedStyle(s.id)}
                          disabled={deletingStyleId === s.id}
                          className="w-full flex items-center justify-center gap-1"
                        >
                          <Trash2 className="h-4 w-4" />
                          {deletingStyleId === s.id ? 'Deleting…' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'vouchers' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex flex-col space-y-2">
              <Button
                onClick={fetchVouchers}
                variant="outline"
                className="flex items-center w-full justify-center"
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh Vouchers
              </Button>

              <div className="flex space-x-2">
                <Input
                  placeholder="Enter voucher code to delete"
                  value={voucherToDelete}
                  onChange={(e) => setVoucherToDelete(e.target.value)}
                  className="flex-1"
                />
                <Button variant="destructive" onClick={deleteVoucher} className="flex items-center">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <VoucherList
              vouchers={vouchers}
              loading={loading}
              expandedVoucherId={expandedVoucherId}
              setExpandedVoucherId={setExpandedVoucherId}
            />
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white px-5 py-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base lg:text-lg font-semibold">Generate New Voucher</h2>
                  <p className="text-xs text-emerald-50/90">
                    Create a discount code for percentage or fixed-amount off.
                  </p>
                </div>
              </div>

              <div className="p-4 lg:p-5 space-y-5">

                {/* Discount type — segmented control */}
                <section className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <Ticket className="h-3.5 w-3.5" /> Discount type
                  </div>
                  <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-gray-100">
                    <button
                      type="button"
                      onClick={() => setNewVoucher({ ...newVoucher, discountType: 'percent' })}
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                        newVoucher.discountType === 'percent'
                          ? 'bg-white shadow-sm text-emerald-700'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Percent className="h-4 w-4" /> Percentage
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Fixed-amount vouchers are single-use with no expiry
                        // by design — force those defaults so the admin can't
                        // drift; the inputs below stay disabled.
                        setNewVoucher({ ...newVoucher, discountType: 'amount', max_uses: 1 });
                        setIsUnlimited(true);
                      }}
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                        newVoucher.discountType === 'amount'
                          ? 'bg-white shadow-sm text-emerald-700'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Euro className="h-4 w-4" /> Fixed amount
                    </button>
                  </div>
                </section>

                {/* Discount value */}
                {newVoucher.discountType === 'percent' ? (
                  <section className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <Percent className="h-3.5 w-3.5" /> Discount percentage
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[20, 40, 60, 100].map((pct) => (
                        <button
                          key={pct}
                          type="button"
                          onClick={() => setNewVoucher({ ...newVoucher, discount_percent: pct })}
                          className={`p-2 rounded-lg border text-sm transition-all ${
                            newVoucher.discount_percent === pct
                              ? 'border-emerald-500 bg-emerald-50 font-semibold text-emerald-700 shadow-sm'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-gray-500">Custom:</span>
                      <div className="relative w-32">
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={newVoucher.discount_percent}
                          onChange={(e) => setNewVoucher({ ...newVoucher, discount_percent: parseInt(e.target.value) || 0 })}
                          className="pr-8 text-base font-semibold"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                      </div>
                    </div>
                  </section>
                ) : (
                  <section className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <Euro className="h-3.5 w-3.5" /> Discount amount
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Object.entries(PRICES).map(([size, price]) => (
                        price > 0 && (
                          <button
                            key={size}
                            type="button"
                            onClick={() => setNewVoucher({ ...newVoucher, discount_amount_cents: Math.round(price * 100) })}
                            className={`p-2 rounded-lg border text-sm transition-all ${
                              newVoucher.discount_amount_cents === Math.round(price * 100)
                                ? 'border-emerald-500 bg-emerald-50 font-semibold text-emerald-700 shadow-sm'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div className="text-[11px] uppercase text-gray-500">{size}</div>
                            <div>€{price.toFixed(2)}</div>
                          </button>
                        )
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-gray-500">Custom:</span>
                      <div className="relative flex-1 max-w-[10rem]">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={(newVoucher.discount_amount_cents / 100).toFixed(2)}
                          onChange={(e) => setNewVoucher({ ...newVoucher, discount_amount_cents: Math.round(parseFloat(e.target.value) * 100) || 0 })}
                          className="pl-7"
                        />
                      </div>
                    </div>
                  </section>
                )}

                <div className="border-t border-gray-100" />

                {/* Limits — uses + validity in one row on wider screens */}
                <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${
                      newVoucher.discountType === 'amount' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      <Hash className="h-3.5 w-3.5" /> Max uses
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={newVoucher.max_uses}
                      disabled={newVoucher.discountType === 'amount'}
                      onChange={(e) => setNewVoucher({ ...newVoucher, max_uses: parseInt(e.target.value) || 1 })}
                      className="disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {newVoucher.discountType === 'amount' && (
                      <p className="text-[11px] text-gray-500">Fixed-amount vouchers are single-use.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${
                      newVoucher.discountType === 'amount' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      <CalendarDays className="h-3.5 w-3.5" /> Validity
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="unlimited"
                        checked={isUnlimited}
                        onCheckedChange={setIsUnlimited}
                        disabled={newVoucher.discountType === 'amount'}
                      />
                      <label
                        htmlFor="unlimited"
                        className={`text-sm cursor-pointer ${newVoucher.discountType === 'amount' ? 'text-gray-400' : ''}`}
                      >
                        Never expires
                      </label>
                    </div>
                    {!isUnlimited && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          value={newVoucher.valid_days}
                          onChange={(e) => setNewVoucher({ ...newVoucher, valid_days: parseInt(e.target.value) || 1 })}
                          className="w-24"
                        />
                        <span className="text-sm text-gray-500">days</span>
                      </div>
                    )}
                  </div>
                </section>

                <div className="border-t border-gray-100" />

                {/* Code shape */}
                <section className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <Ticket className="h-3.5 w-3.5" /> Code
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={newVoucher.prefix}
                      onChange={(e) => setNewVoucher({ ...newVoucher, prefix: e.target.value.toUpperCase() })}
                      placeholder="Prefix (optional)"
                      className="font-mono uppercase tracking-wide flex-1"
                    />
                    <span className="text-gray-400 text-sm">{newVoucher.random ? '-XXXXXX' : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="random"
                      checked={newVoucher.random}
                      onCheckedChange={(checked) => setNewVoucher({ ...newVoucher, random: checked })}
                    />
                    <label htmlFor="random" className="text-xs text-gray-600 cursor-pointer">
                      Append random suffix (recommended)
                    </label>
                  </div>
                  {!newVoucher.random && !newVoucher.prefix.trim() && (
                    <p className="text-xs text-red-500">
                      Set a prefix or enable the random suffix — the code can't be empty.
                    </p>
                  )}
                </section>

                {/* Live preview row — admin sees what they're about to create */}
                <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-700">
                  <span className="font-semibold text-gray-900">Will create:</span>
                  <span className="inline-flex items-center gap-1">
                    {newVoucher.discountType === 'amount' ? (
                      <Euro className="h-3 w-3" />
                    ) : (
                      <Percent className="h-3 w-3" />
                    )}
                    {newVoucher.discountType === 'amount'
                      ? `€${(newVoucher.discount_amount_cents / 100).toFixed(2)} off`
                      : `${newVoucher.discount_percent}% off`}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    {newVoucher.max_uses === 1 ? 'single-use' : `${newVoucher.max_uses} uses`}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    {isUnlimited ? 'never expires' : `${newVoucher.valid_days || 0}d`}
                  </span>
                  {(newVoucher.prefix || newVoucher.random) && (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Ticket className="h-3 w-3" />
                      {newVoucher.prefix || ''}{newVoucher.random ? (newVoucher.prefix ? '-' : '') + 'XXXXXX' : ''}
                    </span>
                  )}
                </div>

                <Button
                  onClick={generateVouchers}
                  disabled={!newVoucher.random && !newVoucher.prefix.trim()}
                  className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0 shadow-md text-sm font-semibold disabled:from-gray-300 disabled:to-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Voucher
                </Button>
              </div>
            </div>
          </div>

          <Dialog
            open={generatedCodes !== null}
            onOpenChange={(open) => {
              if (!open) {
                setGeneratedCodes(null);
                setGeneratedSummary(null);
              }
            }}
          >
            <DialogContent className="bg-white sm:rounded-2xl p-0 overflow-hidden">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white px-6 py-5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                  <PartyPopper className="h-5 w-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-semibold">
                    {generatedCodes && generatedCodes.length > 1
                      ? `${generatedCodes.length} vouchers created`
                      : 'Voucher created'}
                  </DialogTitle>
                  {generatedSummary && (
                    <p className="text-xs text-emerald-50/90 mt-0.5">
                      {generatedSummary.discountType === 'amount'
                        ? `€${(generatedSummary.discountAmountCents / 100).toFixed(2)} off`
                        : `${generatedSummary.discountPercent}% off`}
                      {' · '}
                      {generatedSummary.maxUses === 1
                        ? 'single-use'
                        : `${generatedSummary.maxUses} uses`}
                      {' · '}
                      {generatedSummary.validDays
                        ? `${generatedSummary.validDays} day${generatedSummary.validDays === 1 ? '' : 's'} valid`
                        : 'never expires'}
                    </p>
                  )}
                </div>
              </div>
              <div className="px-6 py-5 space-y-3">
                {(generatedCodes || []).map((code) => (
                  <div
                    key={code}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
                  >
                    <code className="font-mono text-base font-semibold tracking-wide break-all">
                      {code}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyCodeToClipboard(code)}
                      className={`flex-shrink-0 transition-colors ${
                        copiedCode === code ? 'border-emerald-500 text-emerald-700' : ''
                      }`}
                    >
                      {copiedCode === code ? (
                        <>
                          <Check className="h-4 w-4 mr-1" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" /> Copy
                        </>
                      )}
                    </Button>
                  </div>
                ))}
                <p className="text-[11px] text-gray-500 text-center pt-1">
                  Save these codes — they won't be shown again in this form.
                </p>
              </div>
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
                <Button
                  onClick={() => {
                    setGeneratedCodes(null);
                    setGeneratedSummary(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Renders are read from <code>perf.jsonl</code>. Lines without a
              timestamp (older entries) only count toward the all-time totals.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchStats}
              className="rounded-xl"
              disabled={loadingStats}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingStats ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loadingStats && !stats ? (
            <p className="text-sm text-gray-500 text-center py-6">Loading…</p>
          ) : !stats ? (
            <p className="text-sm text-gray-500 text-center py-6">No stats available.</p>
          ) : (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mt-2">Users</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total users" value={stats.users.totalUsers} />
                <StatCard
                  label="Active now"
                  value={stats.users.active5min}
                  hint="last 5 min"
                />
                <StatCard
                  label="Active"
                  value={stats.users.active1h}
                  hint="last 1 h"
                />
                <StatCard
                  label="Active"
                  value={stats.users.active24h}
                  hint="last 24 h"
                />
                <StatCard
                  label="Logins"
                  value={stats.users.logins7d}
                  hint="last 7 days"
                />
                <StatCard
                  label="Logins"
                  value={stats.users.logins30d}
                  hint="last 30 days"
                />
                <StatCard
                  label="New users"
                  value={stats.users.newUsers7d}
                  hint="last 7 days"
                />
                <StatCard
                  label="New users"
                  value={stats.users.newUsers30d}
                  hint="last 30 days"
                />
              </div>

              <h3 className="text-sm font-semibold text-gray-700 mt-4">Generation</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                  label="Generating now"
                  value={stats.users.currentlyGenerating}
                />
                <StatCard
                  label="Total renders"
                  value={stats.renders.totalRenders}
                />
                <StatCard
                  label="Renders 24 h"
                  value={stats.renders.renders24h}
                />
                <StatCard
                  label="Renders 7 d"
                  value={stats.renders.renders7d}
                />
                <StatCard
                  label="Final renders"
                  value={stats.renders.finalRenders}
                  hint="paid resolution"
                />
                <StatCard
                  label="Preview renders"
                  value={stats.renders.previewRenders}
                  hint="dashboard previews"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
                <Histogram
                  title="Generation time"
                  buckets={stats.renders.wallTimeHistogram.buckets}
                  unit={`${stats.renders.wallTimeHistogram.bucketSeconds}s buckets`}
                  formatBucket={(s, isLast) => `${s}–${s + stats.renders.wallTimeHistogram.bucketSeconds}s${isLast ? '+' : ''}`}
                />
                <Histogram
                  title="Peak RAM"
                  buckets={stats.renders.ramHistogram.buckets}
                  unit={`${stats.renders.ramHistogram.bucketGb}GB buckets`}
                  formatBucket={(g, isLast) => `${g}–${g + stats.renders.ramHistogram.bucketGb}GB${isLast ? '+' : ''}`}
                />
              </div>

              <h3 className="text-sm font-semibold text-gray-700 mt-4">Size unlocks</h3>
              {(() => {
                // Defensive against an older backend that doesn't return
                // sizeUnlocks yet — the rest of the tab still renders.
                const u = stats.sizeUnlocks;
                if (!u || !Array.isArray(u.order)) {
                  return <p className="text-xs text-gray-400">No unlocks yet.</p>;
                }
                const totals = u.order.reduce((sum, s) => sum + ((u.total || {})[s] || 0), 0);
                if (totals === 0) {
                  return <p className="text-xs text-gray-400">No unlocks yet.</p>;
                }
                const max = u.order.reduce((m, s) => Math.max(m, (u.total || {})[s] || 0), 0) || 1;
                return (
                  <div className="bg-white border border-gray-200 rounded-2xl p-4 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left font-medium pb-2 pr-2">Size</th>
                          <th className="text-right font-medium pb-2 px-2">24 h</th>
                          <th className="text-right font-medium pb-2 px-2">7 d</th>
                          <th className="text-right font-medium pb-2 px-2">30 d</th>
                          <th className="text-right font-medium pb-2 px-2 whitespace-nowrap">All-time</th>
                          <th className="font-medium pb-2 pl-2 w-1/3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.order.map((size) => {
                          const total = (u.total || {})[size] || 0;
                          const pct = (total / max) * 100;
                          return (
                            <tr key={size} className="border-t border-gray-100">
                              <td className="py-1.5 pr-2 font-medium text-gray-900">{size}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-gray-700">{(u.last24h || {})[size] || 0}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-gray-700">{(u.last7d || {})[size] || 0}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-gray-700">{(u.last30d || {})[size] || 0}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-gray-900 font-medium">{total}</td>
                              <td className="py-1.5 pl-2">
                                <div className="bg-gray-100 rounded h-2 overflow-hidden">
                                  <div className="h-full bg-gray-800" style={{ width: `${pct}%` }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {u.other > 0 && (
                      <p className="text-[10px] text-gray-400 mt-2">
                        + {u.other} unlocks at unrecognised sizes (legacy / custom).
                      </p>
                    )}
                  </div>
                );
              })()}

              <h3 className="text-sm font-semibold text-gray-700 mt-4">Top styles</h3>
              {stats.topStyles.length === 0 ? (
                <p className="text-xs text-gray-400">No style usage tracked yet.</p>
              ) : (
                <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-1">
                  {stats.topStyles.map((s, i) => {
                    const max = stats.topStyles[0].count || 1;
                    const pct = (s.count / max) * 100;
                    return (
                      <div key={s.style} className="flex items-center gap-2 text-xs">
                        <div className="w-6 text-right text-gray-400 tabular-nums">{i + 1}.</div>
                        <div className="w-40 truncate text-gray-700">{s.style}</div>
                        <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                          <div className="h-full bg-gray-800" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-10 text-right text-gray-700 tabular-nums">{s.count}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'cache' && <AdminCachePanel />}

      {activeTab === 'reviews' && (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1 space-y-4">
          <div className="flex items-center gap-2">
            {['pending', 'approved', 'all'].map((f) => (
              <button
                key={f}
                onClick={() => setReviewFilter(f)}
                className={`px-3 py-1.5 rounded-xl text-sm capitalize ${
                  reviewFilter === f
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {f}
              </button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchReviews()}
              className="ml-auto rounded-xl"
              disabled={loadingReviews}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingReviews ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loadingReviews ? (
            <p className="text-sm text-gray-500 text-center py-6">Loading…</p>
          ) : reviews.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No reviews in this view.
            </p>
          ) : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <Card key={r.id} className="border-gray-200">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-3">
                        <Stars value={r.rating} />
                        <span className="text-sm font-medium">
                          {r.displayName || <span className="text-gray-500 italic">Anonymous</span>}
                        </span>
                        {r.approved ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">approved</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">pending</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {r.userEmail} · {r.createdAt && formatSyncDate(r.createdAt)}
                      </div>
                    </div>
                    {r.text && (
                      <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                        {r.text}
                      </p>
                    )}
                    <div className="flex justify-end gap-2 pt-1">
                      {r.approved ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setReviewApproval(r.id, false)}
                          disabled={reviewBusyId === r.id}
                          className="rounded-xl"
                        >
                          <XIcon className="h-4 w-4 mr-1" />
                          Unapprove
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setReviewApproval(r.id, true)}
                          disabled={reviewBusyId === r.id}
                          className="bg-gray-900 text-white rounded-xl"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteReview(r.id)}
                        disabled={reviewBusyId === r.id}
                        className="text-red-600 hover:bg-red-50 rounded-xl"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
